import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { toast } from 'react-toastify'

import * as authApi from '@/services/api/endpoints/auth'
import { UserRole, VerifyOtpResponse } from '@/services/api/endpoints/auth'
import * as tokenStorage from '@/services/api/tokenStorage'
import { ApiError } from '@/services/api/types'
import { fetchCurrentUser } from '@/store/user/userSlice'
import { resetStoreAction } from '@/store/resetAction'

interface AuthenticationState {
  accessToken: string | null
  error: string | null
  isHydrated: boolean
  isLoading: boolean
  isNewUser: boolean | null
  nextStep: string | null
  pendingEmail: string | null
  refreshToken: string | null
  role: UserRole | null
  userId: string | null
}

const initialState: AuthenticationState = {
  accessToken: null,
  error: null,
  isHydrated: false,
  isLoading: false,
  isNewUser: null,
  nextStep: null,
  pendingEmail: null,
  refreshToken: null,
  role: null,
  userId: null,
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof ApiError ? error.message : fallback)

export const login = createAsyncThunk('authentication/login', async (email: string, { rejectWithValue }) => {
  try {
    const response = await authApi.login(email)
    return { email: response.email, nextStep: response.nextStep }
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to send verification code'))
  }
})

export const verifyLoginOtp = createAsyncThunk(
  'authentication/verifyLoginOtp',
  async (payload: { email: string; otp: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authApi.verifyLoginOtp(payload)
      applySession(response)
      if (response.role) dispatch(fetchCurrentUser())
      return response
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Invalid or expired code'))
    }
  },
)

/** For accounts with authenticator-app login enabled - `code` is a live TOTP code or a backup code. */
export const verifyLoginTotp = createAsyncThunk(
  'authentication/verifyLoginTotp',
  async (payload: { code: string; email: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authApi.verifyLoginTotp(payload)
      applySession(response)
      if (response.role) dispatch(fetchCurrentUser())
      return response
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Invalid or expired code'))
    }
  },
)

/** Exchanges a Super-Admin-issued single-use recovery token for a real session. */
export const recoveryLogin = createAsyncThunk(
  'authentication/recoveryLogin',
  async (payload: { email: string; token: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authApi.recoveryLogin(payload)
      applySession(response)
      if (response.role) dispatch(fetchCurrentUser())
      return response
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Invalid or expired recovery code'))
    }
  },
)

export const resendLoginOtp = createAsyncThunk('authentication/resendLoginOtp', async (email: string, { rejectWithValue }) => {
  try {
    await authApi.resendLoginOtp(email)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to resend code'))
  }
})

export const checkRegistrationStatus = createAsyncThunk('authentication/checkRegistrationStatus', async (email: string, { rejectWithValue }) => {
  try {
    return await authApi.registrationStatus(email)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to check registration status'))
  }
})

export const selectRole = createAsyncThunk(
  'authentication/selectRole',
  async (payload: { companyName?: string; institutionId?: string; role: UserRole }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authApi.selectRole(payload)
      applySession(response)
      dispatch(fetchCurrentUser())
      return response
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to select role'))
    }
  },
)

/**
 * Applies a session established outside this slice's own thunks - used by the
 * role-specific register/verify-otp/onboarding flows (student, employer, lawyer,
 * school-admin), which call their endpoints directly rather than through a slice
 * thunk since each role's payload shape differs. Whichever of those steps returns
 * tokens (verify-otp and/or onboarding, depending on the role) dispatches this.
 */
export const establishSession = createAsyncThunk(
  'authentication/establishSession',
  async (response: VerifyOtpResponse, { dispatch }) => {
    applySession(response)
    if (response.role) dispatch(fetchCurrentUser())
    return response
  },
)

export const logout = createAsyncThunk('authentication/logout', async (_, { dispatch }) => {
  try {
    await authApi.logout()
  } catch {
    // best-effort - local state/tokens are cleared regardless
  }
  tokenStorage.clearTokens()
  // Every other slice (immigration, employer, chat, schoolAdmin, superAdmin,
  // studentJobs, subscription, tasks, user) carries data scoped to whoever
  // was logged in - without a full reset, it lingers in memory until the
  // next login happens to overwrite the specific pieces it touches, so a
  // guard or a screen for a slice the new session never fetches can still
  // reflect the account that just logged out.
  dispatch(resetStoreAction())
})

/** Plain reducer (not a thunk) used by the API client's 401-after-failed-refresh handler. */
export const forceLogout = createAsyncThunk('authentication/forceLogout', async (_, { dispatch }) => {
  tokenStorage.clearTokens()
  dispatch(resetStoreAction())
})

export const bootstrapSession = createAsyncThunk('authentication/bootstrapSession', async (_, { dispatch }) => {
  const accessToken = tokenStorage.getAccessToken()
  // A refresh token isn't required to be "logged in" - it only matters for
  // silently renewing an expired access token. Impersonation sessions
  // (see tokenStorage.startImpersonation) deliberately have none, by design:
  // if the access token expires before the admin stops impersonating, the
  // axios refresh interceptor has nothing to refresh with and the session
  // fails closed instead of quietly reverting to the admin's own identity.
  const refreshToken = tokenStorage.getRefreshToken()

  if (!accessToken) {
    return null
  }

  try {
    const me = await dispatch(fetchCurrentUser()).unwrap()
    return { accessToken, refreshToken, role: me.role as UserRole, userId: me.id }
  } catch (error) {
    const { message, status } = (error ?? {}) as { message?: string, status?: number }
    if (status === 401 || status === 403) {
      tokenStorage.clearTokens()
      if (status === 403 && message) toast.error(message)
      return null
    }
    // The server or the connection blipped: that says nothing about their session, so keep it.
    // The dashboard offers a retry, and fetchCurrentUser.fulfilled fills in who they are.
    return { accessToken, refreshToken, role: null, userId: null }
  }
})

/** Persists tokens outside Redux (localStorage) - called from thunks right after a successful auth response. */
function applySession(response: VerifyOtpResponse): void {
  if (response.accessToken && response.refreshToken) {
    tokenStorage.setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken })
  }
}

const authenticationSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        state.pendingEmail = action.payload.email
        state.nextStep = action.payload.nextStep
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(verifyLoginOtp.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        const { accessToken, isNewUser, nextStep, refreshToken, role, userId } = action.payload
        state.isLoading = false
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.userId = userId
        state.role = role
        state.isNewUser = isNewUser
        state.nextStep = nextStep
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(verifyLoginTotp.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(verifyLoginTotp.fulfilled, (state, action) => {
        const { accessToken, isNewUser, nextStep, refreshToken, role, userId } = action.payload
        state.isLoading = false
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.userId = userId
        state.role = role
        state.isNewUser = isNewUser
        state.nextStep = nextStep
      })
      .addCase(verifyLoginTotp.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(recoveryLogin.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(recoveryLogin.fulfilled, (state, action) => {
        const { accessToken, isNewUser, nextStep, refreshToken, role, userId } = action.payload
        state.isLoading = false
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.userId = userId
        state.role = role
        state.isNewUser = isNewUser
        state.nextStep = nextStep
      })
      .addCase(recoveryLogin.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(selectRole.fulfilled, (state, action) => {
        const { accessToken, isNewUser, nextStep, refreshToken, role, userId } = action.payload
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.userId = userId
        state.role = role
        state.isNewUser = isNewUser
        state.nextStep = nextStep
      })
      .addCase(establishSession.fulfilled, (state, action) => {
        const { accessToken, isNewUser, nextStep, refreshToken, role, userId } = action.payload
        if (!accessToken || !refreshToken) return
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.userId = userId
        state.role = role
        state.isNewUser = isNewUser
        state.nextStep = nextStep
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.role ??= action.payload.role
        state.userId ??= action.payload.id
      })
      .addCase(logout.fulfilled, (state) => {
        Object.assign(state, initialState, { isHydrated: true })
      })
      .addCase(forceLogout.fulfilled, (state) => {
        Object.assign(state, initialState, { isHydrated: true })
      })
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        state.isHydrated = true
        if (action.payload) {
          state.accessToken = action.payload.accessToken
          state.refreshToken = action.payload.refreshToken
          state.userId = action.payload.userId
          state.role = action.payload.role
        }
      })
      .addCase(bootstrapSession.rejected, (state) => {
        state.isHydrated = true
      })
  },
  initialState,
  name: 'authentication',
  reducers: {
    clearPendingEmail: (state) => {
      state.pendingEmail = null
      state.nextStep = null
    },
    setPendingEmail: (state, action: PayloadAction<string>) => {
      state.pendingEmail = action.payload
    },
  },
})

export const { clearPendingEmail, setPendingEmail } = authenticationSlice.actions

export const selectAccessToken = (state: { authentication: AuthenticationState }): string | null => state.authentication.accessToken
export const selectRefreshToken = (state: { authentication: AuthenticationState }): string | null => state.authentication.refreshToken
export const selectAuthUserId = (state: { authentication: AuthenticationState }): string | null => state.authentication.userId
export const selectAuthRole = (state: { authentication: AuthenticationState }): UserRole | null => state.authentication.role
export const selectIsNewUser = (state: { authentication: AuthenticationState }): boolean | null => state.authentication.isNewUser
export const selectNextStep = (state: { authentication: AuthenticationState }): string | null => state.authentication.nextStep
export const selectPendingEmail = (state: { authentication: AuthenticationState }): string | null => state.authentication.pendingEmail
export const selectIsAuthHydrated = (state: { authentication: AuthenticationState }): boolean => state.authentication.isHydrated
export const selectAuthIsLoading = (state: { authentication: AuthenticationState }): boolean => state.authentication.isLoading
export const selectAuthError = (state: { authentication: AuthenticationState }): string | null => state.authentication.error

export const selectIsAuthenticated = createSelector(selectAccessToken, (accessToken) => Boolean(accessToken))

export default authenticationSlice.reducer
